import type { SelectOption } from '@krp/ui-kit'

/**
 * Кассовые реквизиты товара своей базы словами магазина. Коды — те же, что хранит служба и отдаёт чек (`OwnFiscal`):
 * ставка НДС — код словаря чека, предмет расчёта — тег 1212 ФФД (1–33), способ расчёта — тег 1214 (1–7). Неизвестный
 * код показывается как есть с пометкой, а не заменяется похожим словом.
 */

/** Ставки, которые можно выбрать. 18 % и 18/118 устарели: их можно увидеть у старого товара, но не выбрать заново. */
export const VAT_RATE_LABELS: Readonly<Record<string, string>> = {
  none: 'Без НДС',
  vat0: 'НДС 0 %',
  vat5: 'НДС 5 %',
  vat7: 'НДС 7 %',
  vat10: 'НДС 10 %',
  vat20: 'НДС 20 %',
  vat22: 'НДС 22 %',
  vat105: 'НДС 5/105 (расчётная)',
  vat107: 'НДС 7/107 (расчётная)',
  vat110: 'НДС 10/110 (расчётная)',
  vat120: 'НДС 20/120 (расчётная)',
  vat122: 'НДС 22/122 (расчётная)',
}

const OUTDATED_VAT_LABELS: Readonly<Record<string, string>> = { vat118: 'НДС 18/118 (устаревшая)' }

/** Предмет расчёта, тег 1212 (ФФД 1.2). 28 и 29 в ФФД не описаны — их нет в выборе. */
export const PAYMENT_SUBJECT_LABELS: Readonly<Record<number, string>> = {
  1: 'Товар',
  2: 'Подакцизный товар',
  3: 'Работа',
  4: 'Услуга',
  5: 'Ставка азартной игры',
  6: 'Выигрыш азартной игры',
  7: 'Лотерейный билет',
  8: 'Выигрыш лотереи',
  9: 'Предоставление РИД',
  10: 'Платёж',
  11: 'Агентское вознаграждение',
  12: 'Выплата',
  13: 'Иной предмет расчёта',
  14: 'Имущественное право',
  15: 'Внереализационный доход',
  16: 'Иные платежи и взносы',
  17: 'Торговый сбор',
  18: 'Туристический (курортный) сбор',
  19: 'Залог',
  20: 'Расход',
  21: 'Взносы на пенсионное страхование ИП',
  22: 'Взносы на пенсионное страхование',
  23: 'Взносы на медицинское страхование ИП',
  24: 'Взносы на медицинское страхование',
  25: 'Взносы на социальное страхование',
  26: 'Платёж казино',
  27: 'Выдача денег банковским платёжным агентом',
  30: 'Подакцизный товар с обязательной маркировкой, без кода (АТНМ)',
  31: 'Подакцизный товар с обязательной маркировкой, с кодом (АТМ)',
  32: 'Товар с обязательной маркировкой, без кода (ТНМ)',
  33: 'Товар с обязательной маркировкой, с кодом (ТМ)',
}

/** Способ расчёта, тег 1214. */
export const PAYMENT_METHOD_LABELS: Readonly<Record<number, string>> = {
  1: 'Предоплата 100 %',
  2: 'Частичная предоплата',
  3: 'Аванс',
  4: 'Полный расчёт',
  5: 'Частичный расчёт и кредит',
  6: 'Передача в кредит',
  7: 'Оплата кредита',
}

export const NOT_SET = 'не указано'

export function vatRateLabel(code: string | null): string {
  if (code === null) return 'Ставка НДС не указана'
  return VAT_RATE_LABELS[code] ?? OUTDATED_VAT_LABELS[code] ?? `Неизвестная ставка «${code}»`
}

export function paymentSubjectLabel(code: number | null): string {
  if (code === null) return NOT_SET
  const label = PAYMENT_SUBJECT_LABELS[code]
  return label === undefined ? `Код ${code} (в ФФД не описан)` : `${label} (${code})`
}

export function paymentMethodLabel(code: number | null): string {
  if (code === null) return NOT_SET
  const label = PAYMENT_METHOD_LABELS[code]
  return label === undefined ? `Код ${code}` : `${label} (${code})`
}

/**
 * Варианты выбора: пустой «не указано» первым; значение, которого нет в списке (устаревшая ставка, неописанный код), остаётся
 * выбранным и видно своим словом — поле не перескакивает молча на другое значение.
 */
export function vatOptions(current: string): readonly SelectOption[] {
  const options: SelectOption[] = [{ value: '', label: 'Выберите ставку' }]
  for (const [value, label] of Object.entries(VAT_RATE_LABELS)) options.push({ value, label })
  if (current !== '' && VAT_RATE_LABELS[current] === undefined) options.push({ value: current, label: vatRateLabel(current) })
  return options
}

/**
 * Весовой признак словами: `''` — не определён (товар не продаётся, пока его не выберут), `'false'` — штучный, `'true'` —
 * весовой. Угаданного «штучного» нет: выбор показан тем, что выбран.
 */
export function weightOptions(): readonly SelectOption[] {
  return [
    { value: '', label: 'Выберите: штучный или весовой' },
    { value: 'false', label: 'Штучный — продаётся штуками' },
    { value: 'true', label: 'Весовой — продаётся на вес' },
  ]
}

export function weightLabel(weighted: boolean | null): string {
  if (weighted === null) return 'не определён — товар не продаётся'
  return weighted ? 'да — продаётся на вес' : 'нет — продаётся штуками'
}

export function subjectOptions(current: string): readonly SelectOption[] {
  return codeOptions(PAYMENT_SUBJECT_LABELS, current, 'Выберите предмет расчёта', paymentSubjectLabel)
}

export function methodOptions(current: string): readonly SelectOption[] {
  return codeOptions(PAYMENT_METHOD_LABELS, current, 'Выберите способ расчёта', paymentMethodLabel)
}

function codeOptions(
  labels: Readonly<Record<number, string>>,
  current: string,
  prompt: string,
  labelOf: (code: number) => string,
): readonly SelectOption[] {
  const options: SelectOption[] = [{ value: '', label: prompt }]
  for (const key of Object.keys(labels)) options.push({ value: key, label: labelOf(Number(key)) })
  if (current !== '' && labels[Number(current)] === undefined) options.push({ value: current, label: labelOf(Number(current)) })
  return options
}
